import Component from '@glimmer/component';

import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';
import { isFalse } from 'ember-route-state/utils/boolean.js';

const hrefNoOp = 'javascript:void(0)';
const relOpen = 'noopener noreferrer';
const targetOpen = '_blank';

/**
 * Replacement for LinkTo/a that uses
 * @property {{}} args Component args
 * @property {boolean} [args.open] True to open link in new window.
 * @property {string|array} [args.route] Route name or transitionTo arguments array.
 * @property {string|array} [args.routeOpen] Route with implied `@open="true"`.
 * @property {*} [args.model] Model for route.
 * @property {array} [args.models] Models for route.
 * @property {object} [args.query] Query-params for route.
 * @property {string} [args.url] URL to navigate to.
 * @property {string} [args.urlOpen] URL with implied `@open="true"`.
 * @property {function} [args.onClick] Called when link clicked.
 * @property {boolean} [args.noOpenIcon] True to hide icon indicating "opens in new window".
 */
export default class LinksToComponent extends Component {
  static anchor;
  static setAnchor(anchor) {
    this.anchor = anchor;
  }

  @service routeState;

  get disabled() {
    return this.args.disabled;
  }
  get url() {
    return this.args.url || this.args.urlOpen;
  }
  get route() {
    return this.args.route || this.args.routeOpen;
  }
  get isOpen() {
    return !!(this.args.open || this.args.routeOpen || this.args.urlOpen);
  }
  @cached get link() {
    return this.url ? null : this.routeState.linkTo({
      route: this.route,
      model: this.args.model,
      models: this.args.models,
      query: this.args.query,
    });
  }
  @cached get href() {
    return this.url || this.link.url || hrefNoOp;
  }
  get isHrefNoOp() {
    return this.href === hrefNoOp;
  }
  @cached get rel() {
    return this.args.rel || this.isOpen && relOpen || undefined;
  }
  @cached get target() {
    return !this.isHrefNoOp && (this.args.target || this.isOpen && targetOpen) || undefined;
  }
  @action onClick(event) {
    if (this.disabled) {
      event.preventDefault();
      return;
    }

    if (!isFalse(this.args.preventDefault)
      && !(this.url || this.isOpen || this.args.defaultLink)
    ) {
      event.preventDefault();
    }

    const { onClick } = this.args;
    if (typeof onClick === 'function') {
      onClick(...arguments);
    }

    if (!this.isOpen) {
      this.link?.transition();
    }
  }

  get anchor() {
    return LinksToComponent.anchor || this.args.anchor || 'a';
  }

  <template>
    <this.anchor @linksTo={{this}} title={{@title}}
      href={{this.href}} rel={{this.rel}} target={{this.target}}
      {{on "click" this.onClick}}
      ...attributes
    >
      {{~yield this~}}
    </this.anchor>
  </template>
}
